import React, { useState, useEffect } from 'react';
import { usersAPI, objectsAPI, objectManagersAPI, referencesAPI } from '../services/api';
import useDialog from '../hooks/useDialog';
import './UsersManagement.css';

const UsersManagement = ({ onClose }) => {
  const [users, setUsers] = useState([]);
  const [objects, setObjects] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [managerObjects, setManagerObjects] = useState({}); // { managerId: [objectIds] }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [assigningUser, setAssigningUser] = useState(null);
  const [selectedObjectIds, setSelectedObjectIds] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'user',
    email: '',
    phone: '',
    organization_id: ''
  });
  
  const currentUserRole = localStorage.getItem('userRole') || 'user';
  const isSuperuser = currentUserRole === 'superuser';

  const mainDialogRef = useDialog({ isOpen: true, onClose });
  const editDialogRef = useDialog({ isOpen: showModal, onClose: () => setShowModal(false) });
  const assignDialogRef = useDialog({ isOpen: showAssignModal, onClose: () => setShowAssignModal(false) });

  useEffect(() => {
    loadUsers();
    loadObjects();
    loadOrganizations();
  }, []);

  useEffect(() => {
    // Загружаем объекты для всех руководителей
    const loadManagerObjects = async () => {
      const managers = users.filter(u => u.role === 'manager');
      const objectsMap = {};
      for (const manager of managers) {
        try {
          const managerObjs = await usersAPI.getObjects(manager.id);
          objectsMap[manager.id] = managerObjs.map(obj => obj.id);
        } catch (err) {
          console.error(`Error loading objects for manager ${manager.id}:`, err);
          objectsMap[manager.id] = [];
        }
      }
      setManagerObjects(objectsMap);
    };
    
    if (users.length > 0) {
      loadManagerObjects();
    }
  }, [users]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await usersAPI.getAll();
      setUsers(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка загрузки пользователей');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadObjects = async () => {
    try {
      const data = await objectsAPI.getAll();
      setObjects(data);
    } catch (err) {
      console.error('Error loading objects:', err);
    }
  };

  const loadOrganizations = async () => {
    try {
      const data = await referencesAPI.getOrganizations();
      setOrganizations(data);
    } catch (err) {
      console.error('Error loading organizations:', err);
    }
  };

  const getOrganizationName = (orgId) => {
    if (!orgId) return '—';
    const org = organizations.find(o => o.id === orgId);
    return org ? org.name : '—';
  };

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({ username: '', password: '', role: 'user', email: '', phone: '', organization_id: '' });
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({ 
      username: user.username, 
      password: '', 
      role: user.role,
      email: user.email || '',
      phone: user.phone || '',
      organization_id: user.organizationId || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этого пользователя?')) {
      return;
    }

    try {
      await usersAPI.delete(userId);
      await loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Ошибка удаления пользователя');
      console.error('Error deleting user:', err);
    }
  };

  const handleAssignObject = (user) => {
    setAssigningUser(user);
    // Получаем текущие объекты руководителя из состояния
    const currentObjectIds = managerObjects[user.id] || [];
    setSelectedObjectIds(currentObjectIds.map(id => id.toString()));
    setShowAssignModal(true);
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const currentObjectIds = (managerObjects[assigningUser.id] || []).map(id => id.toString());
      const selectedIds = selectedObjectIds.map(id => parseInt(id, 10));
      const currentIds = currentObjectIds.map(id => parseInt(id, 10));
      
      // Объекты, которые нужно добавить
      const toAdd = selectedIds.filter(id => !currentIds.includes(id));
      // Объекты, которые нужно убрать
      const toRemove = currentIds.filter(id => !selectedIds.includes(id));
      
      // Обновляем каждый объект с новым списком руководителей
      const allObjectIds = new Set([...toAdd, ...toRemove]);
      
      for (const objId of allObjectIds) {
        const obj = objects.find(o => o.id === objId);
        if (!obj) continue;
        
        // Получаем текущих руководителей объекта
        let currentManagerIds = [];
        try {
          const objManagers = await objectManagersAPI.getManagers(objId);
          currentManagerIds = objManagers.map(m => m.id);
        } catch (err) {
          console.error('Error getting managers for object:', err);
          // Fallback: используем старый manager_id
          if (obj.manager_id) {
            currentManagerIds = [obj.manager_id];
          }
        }
        
        // Обновляем список руководителей
        if (toAdd.includes(objId)) {
          // Добавляем руководителя
          if (!currentManagerIds.includes(assigningUser.id)) {
            currentManagerIds.push(assigningUser.id);
          }
        } else if (toRemove.includes(objId)) {
          // Убираем руководителя
          currentManagerIds = currentManagerIds.filter(id => id !== assigningUser.id);
        }
        
        await objectsAPI.update(objId, obj.name, currentManagerIds);
      }
      
      setShowAssignModal(false);
      await loadObjects();
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка назначения объектов');
      console.error('Error assigning objects:', err);
    }
  };

  const getObjectsForManager = (managerId) => {
    const objectIds = managerObjects[managerId] || [];
    return objects.filter(obj => objectIds.includes(obj.id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const updateData = { 
        username: formData.username, 
        role: formData.role,
        email: formData.email || null,
        phone: formData.phone || null,
        organization_id: formData.organization_id || null
      };
      
      if (editingUser) {
        // Обновление пользователя
        if (formData.password) {
          updateData.password = formData.password;
        }
        await usersAPI.update(editingUser.id, updateData);
      } else {
        // Создание нового пользователя
        if (!formData.password) {
          setError('Пароль обязателен для нового пользователя');
          return;
        }
        await usersAPI.create(formData.username, formData.password, formData.role, formData.email || null, formData.phone || null, formData.organization_id || null);
      }
      setShowModal(false);
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка сохранения пользователя');
      console.error('Error saving user:', err);
    }
  };

  if (loading) {
    return (
      <div className="users-management-overlay">
        <div className="users-management-modal" role="dialog" aria-modal="true" aria-labelledby="users-management-title" tabIndex={-1} ref={mainDialogRef}>
          <div className="users-management-header">
            <h2 id="users-management-title">Управление пользователями</h2>
            <button className="close-btn" onClick={onClose} aria-label="Закрыть">×</button>
          </div>
          <div className="users-management-content">
            <p>Загрузка...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="users-management-overlay">
      <div className="users-management-modal" role="dialog" aria-modal="true" aria-labelledby="users-management-title" tabIndex={-1} ref={mainDialogRef}>
        <div className="users-management-header">
          <h2 id="users-management-title">Управление пользователями</h2>
          <button className="close-btn" onClick={onClose} aria-label="Закрыть">×</button>
        </div>
        <div className="users-management-content">
          {error && <div className="error-message" role="alert">{error}</div>}
          
          <div className="users-management-actions">
            <button className="btn-primary" onClick={handleCreate}>
              + Добавить пользователя
            </button>
          </div>

          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th scope="col">ID</th>
                  <th scope="col">Имя пользователя</th>
                  <th scope="col">Роль</th>
                  <th scope="col">Email</th>
                  <th scope="col">Телефон</th>
                  <th scope="col">Организация</th>
                  <th scope="col">Объекты</th>
                  <th scope="col">Дата создания</th>
                  <th scope="col">Действия</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '20px' }}>
                      Нет пользователей
                    </td>
                  </tr>
                ) : (
                  users.map(user => {
                    const userObjects = user.role === 'manager' ? getObjectsForManager(user.id) : [];
                    return (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>{user.username}</td>
                        <td>
                          <span className={`role-badge ${
                            user.role === 'superuser' ? 'role-superuser' : 
                            user.role === 'admin' ? 'role-admin' : 
                            user.role === 'manager' ? 'role-manager' : 
                            'role-user'
                          }`}>
                            {user.role === 'superuser' ? 'Суперпользователь' : 
                             user.role === 'admin' ? 'Администратор' : 
                             user.role === 'manager' ? 'Руководитель объекта' : 
                             'Пользователь'}
                          </span>
                        </td>
                        <td>{user.email || '—'}</td>
                        <td>{user.phone || '—'}</td>
                        <td>{user.organizationName || '—'}</td>
                        <td>
                          {user.role === 'manager' ? (
                            userObjects.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {userObjects.map(obj => (
                                  <span key={obj.id} className="object-assigned">{obj.name}</span>
                                ))}
                              </div>
                            ) : (
                              <span className="object-not-assigned">Не назначен</span>
                            )
                          ) : (
                            <span className="object-na">—</span>
                          )}
                        </td>
                        <td>{new Date(user.created_at).toLocaleDateString('ru-RU')}</td>
                        <td>
                          <div className="action-buttons">
                            {user.role === 'manager' && (
                              <button 
                                className="btn-assign" 
                                onClick={() => handleAssignObject(user)}
                                title="Назначить объекты"
                                aria-label={`Назначить объекты: ${user.username}`}
                              >
                                📋
                              </button>
                            )}
                            <button 
                              className="btn-edit" 
                              onClick={() => handleEdit(user)}
                              title="Редактировать"
                              aria-label={`Редактировать: ${user.username}`}
                            >
                              ✏️
                            </button>
                            <button 
                              className="btn-delete" 
                              onClick={() => handleDelete(user.id)}
                              title="Удалить"
                              aria-label={`Удалить: ${user.username}`}
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="users-management-overlay">
          <div className="users-management-modal modal-small" role="dialog" aria-modal="true" aria-labelledby="user-form-title" tabIndex={-1} ref={editDialogRef}>
            <div className="users-management-header">
              <h2 id="user-form-title">{editingUser ? 'Редактировать пользователя' : 'Создать пользователя'}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)} aria-label="Закрыть">×</button>
            </div>
            <form onSubmit={handleSubmit} className="user-form">
              {error && <div className="error-message" role="alert">{error}</div>}
              
              <div className="form-group">
                <label htmlFor="user-username">Имя пользователя:</label>
                <input
                  id="user-username"
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  autoComplete="username"
                />
              </div>

              <div className="form-group">
                <label htmlFor="user-password">Пароль{editingUser ? ' (оставьте пустым, чтобы не менять)' : ''}:</label>
                <input
                  id="user-password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  autoComplete={editingUser ? 'new-password' : 'new-password'}
                />
              </div>

              <div className="form-group">
                <label htmlFor="user-role">Роль:</label>
                <select
                  id="user-role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <option value="user">Пользователь</option>
                  <option value="manager">Руководитель объекта</option>
                  {isSuperuser && <option value="admin">Администратор</option>}
                  {isSuperuser && <option value="superuser">Суперпользователь</option>}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="user-email">Email:</label>
                <input
                  id="user-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="example@mail.ru"
                />
              </div>

              <div className="form-group">
                <label htmlFor="user-phone">Телефон:</label>
                <input
                  id="user-phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+7 (999) 123-45-67"
                />
              </div>

              <div className="form-group">
                <label htmlFor="user-organization">Организация:</label>
                <select
                  id="user-organization"
                  value={formData.organization_id}
                  onChange={(e) => setFormData({ ...formData, organization_id: e.target.value })}
                >
                  <option value="">Не выбрано</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Отмена
                </button>
                <button type="submit" className="btn-primary">
                  {editingUser ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAssignModal && assigningUser && (
        <div className="users-management-overlay">
          <div className="users-management-modal modal-small" role="dialog" aria-modal="true" aria-labelledby="assign-title" tabIndex={-1} ref={assignDialogRef}>
            <div className="users-management-header">
              <h2 id="assign-title">Назначить объекты руководителю: {assigningUser.username}</h2>
              <button className="close-btn" onClick={() => setShowAssignModal(false)} aria-label="Закрыть">×</button>
            </div>
            <form onSubmit={handleAssignSubmit} className="user-form">
              {error && <div className="error-message" role="alert">{error}</div>}
              
              <fieldset className="form-group">
                <legend>Выберите объекты (можно несколько)</legend>
                <div className="objects-checkbox-list" style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '10px' }}>
                  {objects.length === 0 ? (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#999' }}>
                      Нет доступных объектов
                    </div>
                  ) : (
                    objects.map(obj => {
                      const isChecked = selectedObjectIds.includes(obj.id.toString());
                      // Проверяем, назначен ли объект другим руководителям
                      const otherManagers = Object.keys(managerObjects)
                        .filter(mid => parseInt(mid) !== assigningUser.id && managerObjects[mid].includes(obj.id))
                        .map(mid => users.find(u => u.id === parseInt(mid))?.username)
                        .filter(Boolean);
                      
                      return (
                        <label key={obj.id} style={{ display: 'flex', alignItems: 'center', padding: '8px', cursor: 'pointer', borderRadius: '4px', marginBottom: '4px', backgroundColor: isChecked ? '#e3f2fd' : 'transparent' }}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedObjectIds([...selectedObjectIds, obj.id.toString()]);
                              } else {
                                setSelectedObjectIds(selectedObjectIds.filter(id => id !== obj.id.toString()));
                              }
                            }}
                            style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: isChecked ? '600' : '400' }}>{obj.name}</div>
                            {otherManagers.length > 0 && (
                              <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                                Также назначен: {otherManagers.join(', ')}
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  Выбрано: {selectedObjectIds.length} из {objects.length}
                </div>
              </fieldset>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowAssignModal(false)}>
                  Отмена
                </button>
                <button type="submit" className="btn-primary">
                  Сохранить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;
